import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  Children,
  isValidElement,
  cloneElement,
  type ReactNode,
  type CSSProperties,
} from "react";

/**
 * Reveal / RevealGroup
 *
 * Fade + translate on scroll-in. Prefers a single RevealGroup wrapping a
 * cohesive block of content (label + heading + intro) over three sibling
 * Reveals with staggered delays. One thought reveals as one unit.
 *
 * Honors prefers-reduced-motion.
 */

const RevealGroupContext = createContext<{ inView: boolean; base: number } | null>(null);

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

function useInView<T extends Element>() {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, inView };
}

function revealStyle(inView: boolean, delay: number, reduced: boolean): CSSProperties {
  if (reduced) {
    return { opacity: inView ? 1 : 0, transition: "opacity 200ms linear", transitionDelay: `${delay}ms` };
  }
  return {
    opacity: inView ? 1 : 0,
    transform: inView ? "translateY(0)" : "translateY(14px)",
    transition:
      "opacity 520ms cubic-bezier(0.2,0.7,0.1,1), transform 520ms cubic-bezier(0.2,0.7,0.1,1)",
    transitionDelay: `${delay}ms`,
    willChange: "opacity, transform",
  };
}

export function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduced = usePrefersReducedMotion();
  const group = useContext(RevealGroupContext);
  const { ref, inView } = useInView<HTMLDivElement>();

  if (group) {
    return (
      <div className={className} style={revealStyle(group.inView, group.base + delay, reduced)}>
        {children}
      </div>
    );
  }
  return (
    <div ref={ref} className={className} style={revealStyle(inView, delay, reduced)}>
      {children}
    </div>
  );
}

/**
 * RevealGroup: reveals its direct children as one unit with a tiny internal
 * cascade. Use to replace stacked <Reveal delay={0/80/160}> siblings.
 *
 * `stagger` is per-child ms (default 60), capped at `maxStagger` total.
 */
export function RevealGroup({
  children,
  stagger = 60,
  maxStagger = 240,
  as: Tag = "div",
  className,
}: {
  children: ReactNode;
  stagger?: number;
  maxStagger?: number;
  as?: "div" | "section";
  className?: string;
}) {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>();
  const kids = Children.toArray(children).filter(Boolean);

  return (
    <Tag ref={ref as never} className={className}>
      {kids.map((child, i) => {
        const delay = Math.min(i * stagger, maxStagger);
        // If child is already a <Reveal>, pass through via context.
        if (isValidElement(child) && (child.type === Reveal)) {
          return (
            <RevealGroupContext.Provider key={i} value={{ inView, base: delay }}>
              {child}
            </RevealGroupContext.Provider>
          );
        }
        return (
          <div key={i} style={revealStyle(inView, delay, reduced)}>
            {child}
          </div>
        );
      })}
    </Tag>
  );
}

export function Label({ n, text }: { n: string; text: string }) {
  return (
    <div className="flex items-center gap-4 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-muted-foreground">
      <span className="text-sun">{n}</span>
      <span className="h-px w-10 bg-border" />
      {text && <span>{text}</span>}
    </div>
  );
}

/* Shared typography scale. One source of truth for template pages. */
export const HEADING = {
  h1: "font-display font-black uppercase leading-[0.95] text-[clamp(2rem,4.5vw,4.5rem)]",
  h2: "font-display font-black uppercase leading-[0.95] text-[clamp(1.75rem,3.8vw,3.5rem)]",
  h3: "font-display font-black uppercase leading-tight text-xl md:text-2xl",
  lead: "text-lg leading-relaxed text-muted-foreground max-w-[62ch]",
  body: "text-base leading-relaxed text-muted-foreground max-w-[68ch]",
} as const;
